import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useMediaQuery } from '@mui/material';
import _ from 'lodash';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faFacebookSquare,
  faSquareXTwitter,
  faYoutubeSquare,
} from '@fortawesome/free-brands-svg-icons';

import Login from '../Login/Login';
import UserAccountCreation from '../UserAccountCreation/UserAccountCreation';

import { getActiveConvention } from 'actions/ConventionActions/ConventionActions';

import logo from 'utils/images/AFT-Square-LogoBlue-TagWhite.png';
import smallLogo from 'utils/images/TagLogo_AllWhite.png';
import dcImage from 'utils/images/landing-page-background-img.jpg';

interface ActiveConventionType {
  title?: string;
  tagline?: string;
  hashtag?: string;
  registrationClosed?: boolean;
}

const LandingPage: React.FC = () => {
  const navigate = useNavigate();

  // Responsive breakpoints
  const isMobile = useMediaQuery('(max-width:767px)');
  const isTabletPortrait = useMediaQuery('(min-width:768px) and (max-width:1023px)');
  const isTabletLandscape = useMediaQuery('(min-width:1024px) and (max-width:1439px)');

  const [activeView, setActiveView] = useState<'login' | 'register'>('register');
  const [activeConvention, setActiveConvention] = useState<ActiveConventionType | null>(null);
  const [conventionLoading, setConventionLoading] = useState(false);
  const [conventionLoadAttempted, setConventionLoadAttempted] = useState(false);
  useEffect(() => {
    if (window.sessionStorage.getItem('loggedIn') === 'true') {
      navigate('/portal');
    }
  }, [navigate]);

  useEffect(() => {
    if (!activeConvention && !conventionLoading && !conventionLoadAttempted) {
      setConventionLoading(true);
      setConventionLoadAttempted(true);
      getActiveConvention().then(
        (resp) => {
          setConventionLoading(false);
          setActiveConvention(resp.data);
        },
        (error) => {
          console.log(error);
          setConventionLoading(false);
        }
      );
    }
  }, [activeConvention, conventionLoading, conventionLoadAttempted]);

  const toggleView = () => {
    setActiveView(activeView === 'login' ? 'register' : 'login');
  };

  const renderNoticeIframe = () => (
    <div className="landing-notice-container">
      <iframe
        src={`${__API_URL__}api/proxy/connect-notice`}
        sandbox="allow-scripts allow-popups allow-popups-to-escape-sandbox"
        width="100%"
        height="100%"
        style={{ border: 'none', borderRadius: '4px' }}
      />
    </div>
  );

  const renderHeader = () => (
    <header className="landing-header">
      <div className="landing-header-logo">
        <img
          src={isMobile ? smallLogo : logo}
          alt="American Federation of Teachers Logo"
          className="landing-logo-img"
        />
      </div>
      <div className="landing-header-title">
        <h1>{activeConvention?.title || 'AFT Convention 2026'}</h1>
      </div>
    </header>
  );

  const renderFooter = () => (
    <footer className="landing-footer">
      <div className="landing-footer-copyright">
        <span>&copy; 2024 AFT, AFL-CIO. All rights reserved.</span>
      </div>
      <div className="landing-footer-social">
        <a
          href="https://www.facebook.com/AFTunion"
          target="_blank"
          rel="noreferrer"
          aria-label="Facebook"
        >
          <FontAwesomeIcon icon={faFacebookSquare} size="lg" />
        </a>
        <a
          href="https://twitter.com/AFTunion"
          target="_blank"
          rel="noreferrer"
          aria-label="Twitter"
        >
          <FontAwesomeIcon icon={faSquareXTwitter} size="lg" />
        </a>
        <a
          href="https://www.youtube.com/user/AFTHQ"
          target="_blank"
          rel="noreferrer"
          aria-label="YouTube"
        >
          <FontAwesomeIcon icon={faYoutubeSquare} size="lg" />
        </a>
      </div>
    </footer>
  );

  const renderLoginForm = () => (
    <div className="landing-form-section">
      <Login
        isMobile={isMobile}
        onSwitchToRegister={toggleView}
      />
    </div>
  );

  const renderRegistrationForm = () => (
    <div className="landing-form-section">
      <UserAccountCreation
        registrationClosed={_.get(activeConvention, 'registrationClosed') === true}
        conventionLoadPending={conventionLoading}
        onSwitchToLogin={toggleView}
        isLandingPage={true}
      />
    </div>
  );

  // Mobile Layout
  if (isMobile) {
    return (
      <div className="landing-page landing-page--mobile">
        <div className="landing-mobile-header">
          <img
            src={smallLogo}
            alt="AFT Logo"
            className="landing-mobile-logo"
          />
          <h1 className="landing-mobile-title">
            {activeConvention?.title || 'AFT Convention 2026'}
          </h1>
        </div>

        <main className="landing-mobile-main">
          {activeView === 'login' ? renderLoginForm() : renderRegistrationForm()}

          <div className="landing-mobile-notice">
            {renderNoticeIframe()}
          </div>
        </main>

        {renderFooter()}
      </div>
    );
  }

  // Tablet Portrait Layout
  if (isTabletPortrait) {
    return (
      <div className="landing-page landing-page--tablet-portrait">
        {renderHeader()}

        <main className="landing-main">
          <div className="landing-image-column">
            <img src={dcImage} alt="Washington DC" className="landing-bg-image" />
          </div>

          <div className="landing-content-column">
            <div className="landing-form-wrapper">
              {activeView === 'login' ? renderLoginForm() : renderRegistrationForm()}
            </div>

            <div className="landing-notice-wrapper">
              {renderNoticeIframe()}
            </div>
          </div>
        </main>

        {renderFooter()}
      </div>
    );
  }

  // Tablet Landscape Layout
  if (isTabletLandscape) {
    return (
      <div className="landing-page landing-page--tablet-landscape">
        {renderHeader()}

        <main className="landing-main">
          <div className="landing-image-column">
            <img src={dcImage} alt="Washington DC" className="landing-bg-image" />
          </div>

          <div className="landing-content-column">
            <div className="landing-form-wrapper">
              {activeView === 'login' ? renderLoginForm() : renderRegistrationForm()}
            </div>

            <div className="landing-notice-wrapper">
              {renderNoticeIframe()}
            </div>
          </div>
        </main>

        {renderFooter()}
      </div>
    );
  }

  // Desktop Layout (default)
  return (
    <div className="landing-page landing-page--desktop">
      {renderHeader()}

      <main className="landing-main">
        <div className="landing-image-column">
          <img src={dcImage} alt="Washington DC" className="landing-bg-image" />
        </div>

        <div className="landing-form-column">
          {activeView === 'login' ? renderLoginForm() : renderRegistrationForm()}
        </div>

        <div className="landing-notice-column">
          {renderNoticeIframe()}
        </div>
      </main>

      {renderFooter()}
    </div>
  );
};

export default LandingPage;
